/**
 * The address bar follows the app state (maintainer feedback, Sprint 3): after any change of the date, the scale
 * mode, focus or follow, the selected mission or the playback, the query is replaced so that the address is always a
 * direct link to what is on screen. `parseDeepLink` reads it back. Parameters the app does not own (debug, fps,
 * quality, lang, intro) are kept as they are.
 */
import { get } from 'svelte/store';
import type { ScaleMode } from '../core/scaleProfiles';
import { SPEEDS, currentDate, focus, followTarget, introActive, playback, scaleMode, selectedMission } from './stores';

export interface LinkState {
  /** UTC ms. */
  t: number;
  mode: ScaleMode;
  focus: string;
  follow: string | null;
  mission: string | null;
  /** Simulated seconds per real second. */
  speed: number;
  playing: boolean;
}

/** Parameters written by the sync; everything else in the query is left alone. */
const OWNED = ['t', 'mode', 'focus', 'follow', 'mission', 'speed', 'play'];
const DEFAULT_SPEED = SPEEDS[4].value;

/** `1979-07-09T22:29Z`: minutes, like the date display (parseUtc reads it). */
export function formatLinkTime(ms: number): string {
  return new Date(Math.floor(ms / 60_000) * 60_000).toISOString().slice(0, 16) + 'Z';
}

/** The query for `state`, with the foreign parameters of `existing` after the app's own. Defaults are left out. */
export function buildSearch(state: LinkState, existing: string): string {
  const q = new URLSearchParams();
  q.set('t', formatLinkTime(state.t));
  if (state.mode !== 'overview') q.set('mode', state.mode);
  if (state.follow) q.set('follow', state.follow);
  else if (state.focus !== 'sun') q.set('focus', state.focus);
  if (state.mission) q.set('mission', state.mission);
  const speed = SPEEDS.find((s) => s.value === state.speed);
  if (speed && speed.value !== DEFAULT_SPEED) q.set('speed', speed.id);
  if (state.playing) q.set('play', '1');
  for (const [key, value] of new URLSearchParams(existing)) if (!OWNED.includes(key)) q.append(key, value);
  // keep the address readable: ':' needs no escaping in a query
  return `?${q.toString().replace(/%3A/g, ':')}`;
}

/** Throttle while the date runs (one write a second) and after single changes (a quarter second). */
const PLAYING_MS = 1000;
const IDLE_MS = 250;

/**
 * Starts writing the state into the address bar, from the first change after now (an untouched start keeps its
 * address). Returns a function that stops it.
 */
export function startUrlSync(): () => void {
  let timer: ReturnType<typeof setTimeout> | null = null;
  let armed = false;
  const write = (): void => {
    timer = null;
    if (get(introActive)) return;
    const pb = get(playback);
    const state: LinkState = {
      t: get(currentDate),
      mode: get(scaleMode),
      focus: get(focus),
      follow: get(followTarget),
      mission: get(selectedMission),
      speed: pb.speed,
      playing: pb.playing,
    };
    const search = buildSearch(state, window.location.search);
    if (search !== window.location.search) {
      history.replaceState(history.state, '', `${window.location.pathname}${search}${window.location.hash}`);
    }
  };
  const changed = (): void => {
    if (!armed || timer !== null) return;
    timer = setTimeout(write, get(playback).playing ? PLAYING_MS : IDLE_MS);
  };
  const stores = [currentDate, scaleMode, focus, followTarget, selectedMission, playback];
  const unsubscribers = stores.map((s) => s.subscribe(changed));
  armed = true;
  return () => {
    unsubscribers.forEach((u) => u());
    if (timer !== null) clearTimeout(timer);
  };
}
